/** @jsxImportSource @emotion/react */
import { Paper as MuiPaper, useMediaQuery, useTheme } from '@material-ui/core';
import { css } from 'global';
import { FC, useEffect } from 'react';
import * as styles from './SendGuide.styles';
import PhoneInput from 'components/phone-input';
import * as Icons from '@zip/icons';
import { SendGuideContent, SendGuideProps } from './SendGuideProps';
import { useFetch } from 'hooks';
import { logError, logEvent } from 'utils';
import { PageAction } from 'enums';
import { useSnackbar } from 'contexts';
import hiFive from 'public/plan-to-purchase/Hi_Five.svg';
import dogCollar from 'public/plan-to-purchase/Dog_Collar.svg';

const SendGuideModule: FC<SendGuideProps> = ({
  category,
  merchantUniqueId,
}) => {
  const Snackbar = useSnackbar();
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('xs'));
  const { post } = useFetch();

  const contentDefinition: SendGuideContent[] = [
    {
      imagePath: dogCollar,
      category: 'vet',
      subHeading: "Your pet owners' guide to paying with Zip",
      placeholder: isMobile ? 'Phone Number' : "Pet owners' Phone Number",
      text: 'Unlock more sales for your business and support your pet owners by sharing our path to purchase guide.',
      path: '/au/pos/zip-money-vet',
    },
    {
      imagePath: hiFive,
      category: 'dental',
      subHeading: "Your patients' guide to paying with Zip",
      placeholder: isMobile ? 'Phone Number' : "Patients' Phone Number",
      text: 'Unlock more sales for your business and support your patients by sharing our path to purchase guide.',
      path: '/au/pos/zip-money-dental',
    },
  ];

  const content = contentDefinition.find((element) => {
    if (category === 'ndp') {
      return element.category === 'dental';
    }
    return element.category === category;
  });

  const baseUrl = process.env.REACT_APP_ZIP_URL;
  const link = `${baseUrl}${content.path}?m=${merchantUniqueId}${
    category === 'ndp' ? '&referrer=ndp' : ''
  }`;

  useEffect(() => {
    logEvent(PageAction.purchasePlan, {
      category: category,
      merchantUniqueId: merchantUniqueId,
    });
  }, []);

  async function sendGuide(mobilePhone: string): Promise<void> {
    try {
      post('/customer/send-plan-to-purchase', { mobilePhone });

      logEvent(PageAction.clickedPurchasePlanSend, {
        category: category,
        merchantUniqueId: merchantUniqueId,
      });
    } catch (err) {
      logError(PageAction.clickedPurchasePlanSend, err, {
        category: category,
        merchantUniqueId: merchantUniqueId,
      });

      Snackbar.error(
        'An error occured processing your request. Please try again.'
      );

      throw err;
    }
  }

  function postOpenGuideLink(): void {
    logEvent(PageAction.clickedOpenPurchasePlanLink, {
      category: category,
      merchantUniqueId: merchantUniqueId,
    });
  }

  return (
    <MuiPaper elevation={0} css={styles.container}>
      <div>
        <img src={content.imagePath} width={64} title="logo" alt="Logo" />
      </div>
      <div css={css.moduleTitleBar} style={{ marginTop: 12 }}>
        <span className="heading1" css={styles.heading}>
          {content.subHeading}
        </span>
      </div>
      <div style={{ marginTop: 12 }}>
        <span className="body2" css={styles.subHeading}>
          {content.text}
        </span>
      </div>
      <div css={styles.input}>
        <PhoneInput
          placeholder={content.placeholder}
          preButtonText={'Send guide'}
          postButtonText={'Guide sent'}
          onSubmit={sendGuide}
        />
      </div>
      <div css={styles.linkStyle}>
        <a
          href={link}
          onClick={(): void => postOpenGuideLink()}
          target="_blank"
        >
          Open link{' '}
          <Icons.ArrowSquareOut
            style={{ fontSize: 20, verticalAlign: 'top' }}
          />
        </a>
      </div>
    </MuiPaper>
  );
};

export default SendGuideModule;
